import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Loader2, Pencil, Trash2, Tag, Check, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Categories() {
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const canManageGlobals = user.role === "admin" || user.role === "manager";
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/categories"); setCats(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/categories", { name });
      toast.success("Category added");
      setOpen(false); setName(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/categories/${id}`, { name: editName });
      toast.success("Category updated");
      setEditingId(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/categories/${id}`); toast.success("Category deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggleHidden = async (c) => {
    try {
      await api.post(`/categories/${c.id}/${c.hidden ? "unhide" : "hide"}`);
      toast.success(c.hidden ? "Category shown to your team" : "Category hidden from your team");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const scopeLabel = isSuper ? "Global expense categories used across all tenants." : "Global categories (you can hide the ones you don't use) plus your tenant's custom categories.";

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Categories</h1>
          <p className="text-sm text-muted-foreground mt-1">{scopeLabel}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="new-category-btn"><Plus className="h-4 w-4 mr-2" />New Category</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{isSuper ? "New Global Category" : "New Tenant Category"}</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Category name" data-testid="category-name" className="rounded-sm" />
              <DialogFooter>
                <Button type="submit" disabled={busy} className="rounded-sm" data-testid="category-submit">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-border rounded-md">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : cats.length === 0 ? (
          <div className="p-12 text-center"><Tag className="h-10 w-10 mx-auto text-muted-foreground mb-3" /><p className="text-sm text-muted-foreground">No categories yet.</p></div>
        ) : (
          <ul className="divide-y divide-border">
            {cats.map((c) => (
              <li key={c.id} className="flex items-center justify-between px-5 py-3 hover:bg-muted/50 transition-colors" data-testid={`category-row-${c.id}`}>
                {editingId === c.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-sm max-w-xs" data-testid={`category-edit-input-${c.id}`} />
                    <Button size="icon" variant="ghost" onClick={() => saveEdit(c.id)} data-testid={`category-save-${c.id}`}><Check className="h-4 w-4 text-success" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setEditingId(null)}><X className="h-4 w-4" /></Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <Tag className={`h-4 w-4 ${c.hidden ? "text-muted-foreground/50" : "text-muted-foreground"}`} />
                      <span className={`text-sm font-medium ${c.hidden ? "text-muted-foreground line-through" : ""}`}>{c.name}</span>
                      <Badge variant="outline" className={`rounded-full text-xs ${c.scope === "global" ? "bg-info/10 text-info border-info/30" : "bg-warning/10 text-warning border-warning/30"}`}>
                        {c.scope === "global" ? "Global" : "Tenant"}
                      </Badge>
                      {c.hidden && <Badge variant="outline" className="rounded-full text-xs bg-muted text-muted-foreground">Hidden</Badge>}
                    </div>
                    <div className="flex items-center gap-1">
                      {canManageGlobals && c.scope === "global" && (
                        <Button size="sm" variant="ghost" className="rounded-sm text-xs" onClick={() => toggleHidden(c)} data-testid={`category-toggle-hidden-${c.id}`}>
                          {c.hidden ? <><Eye className="h-4 w-4 mr-1.5" />Show</> : <><EyeOff className="h-4 w-4 mr-1.5" />Hide</>}
                        </Button>
                      )}
                      {c.editable && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => { setEditingId(c.id); setEditName(c.name); }} data-testid={`category-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" data-testid={`category-delete-${c.id}`}><Trash2 className="h-4 w-4 text-danger" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete "{c.name}"?</AlertDialogTitle>
                                <AlertDialogDescription>This category will no longer be available for new claims. Existing claims are unaffected.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                <AlertDialogAction className="rounded-sm bg-danger hover:bg-danger/90" onClick={() => remove(c.id)} data-testid={`category-confirm-delete-${c.id}`}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
